import { TranslateService } from '@ngx-translate/core';

// Shared by tracker-configs.component.ts (grid/global view cards),
// tracker-config-detail-dialog.component.ts (read-only detail) and
// tracker-settings-overview-dialog.component.ts (multi-select "view settings") - a template's
// `settings` shape is entirely different per type (KYD distances/groups, Sigfox
// payload/bodyTemplate, everything else just free "Custom Settings" key/value pairs), so all
// three render it the same generic way rather than duplicating per-type formatting logic.

// Same i18n keys as the field labels shown in the actual editable form (tracker-config-form.
// component.ts) - a field with a known, named UI input elsewhere shows the same label here
// instead of its raw camelCase property name (e.g. "Distance d'auto-alerte" instead of
// "minDistance").
const KYD_FIELD_LABEL_KEYS: Record<string, string> = {
  userMode: 'units.device.mode',
  minDistance: 'trackers.selfAlertDistance',
  maxDistance: 'trackers.otherAlertDistance',
  friendGroup: 'trackers.friendGroup',
  ignoreGroup: 'trackers.ignoreGroup',
  unfriendDistance: 'trackers.unfriendDistanceLabel',
  exclusionDistance: 'trackers.exclusionDistanceLabel',
};

const SIGFOX_FIELD_LABEL_KEYS: Record<string, string> = {
  payloadConfig: 'units.device.payload_config',
  bodyTemplate: 'units.device.body_template',
};

function fieldLabelKeysFor(type: string | undefined): Record<string, string> | undefined {
  if (type === 'KYD') return KYD_FIELD_LABEL_KEYS;
  if (type === 'Sigfox') return SIGFOX_FIELD_LABEL_KEYS;
  return undefined;
}

// A custom field is stored as {label, name, type, value} (see CustomInput, tracker-config-form.
// component.ts's getCustomFields()) - shown by its label. A recognized KYD/Sigfox field (see
// above) is shown by its translated form label. Anything else (a genuinely custom/unknown key)
// falls back to the raw key, same as before.
export function trackerConfigSettingsEntries(settings: any, translate?: TranslateService, type?: string): { key: string; value: string }[] {
  if (!settings) return [];

  const labelKeys = fieldLabelKeysFor(type);

  return Object.entries(settings)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => {
      if (value != null && typeof value === 'object' && 'label' in value) {
        return { key: (value as any).label || key, value: String((value as any).value ?? '') };
      }

      const labelKey = labelKeys?.[key];
      const label = labelKey && translate ? translate.instant(labelKey) : key;

      if (Array.isArray(value)) return { key: label, value: value.join(', ') };
      if (value != null && typeof value === 'object') return { key: label, value: JSON.stringify(value) };
      return { key: label, value: value == null ? '' : String(value) };
    });
}

// TrackerConfig has no createdDate field of its own (same gap as Tracker - see
// resources.component.ts's trackerCreatedAt()) - a Mongo ObjectId's first 4 bytes are a Unix
// timestamp (seconds) of when it was created, so this needs no backend/migration to work.
export function trackerConfigCreatedAt(config: { id?: string } | null | undefined): number {
  const id = config?.id;
  return typeof id === 'string' && id.length >= 8 ? parseInt(id.substring(0, 8), 16) * 1000 : 0;
}

// The backend enforces this too (TrackerConfigSchema's `name: {unique: true}`), but only ever
// with a raw, untranslated Mongo duplicate-key error - checked client-side first everywhere a
// template gets named (the main manager's create/edit form, every "Enregistrer comme modèle"
// flow) for a friendly, immediate message instead. `excludeId` is the config being edited/
// updated itself (never a conflict with its own unchanged name).
export function isDuplicateTemplateName(configs: { id: string; name: string }[] | null | undefined, name: string | null | undefined, excludeId?: string): boolean {
  const trimmed = name?.trim();
  if (!trimmed) return false;
  return (configs || []).some(c => c.name === trimmed && c.id !== excludeId);
}
